// Google OAuth authorization code flow with PKCE + refresh tokens.
// Session hard-capped at 7 days from the original grant.
//
// Storage split:
//   sessionStorage borg_token   — short-lived id_token blob (per-tab, gone on close)
//   localStorage   borg_refresh — refresh_token + 7-day anchor (survives tab close)
//
// On fresh tab open we re-hydrate sessionStorage from localStorage by
// exchanging the refresh_token for a new id_token — so the 7-day session
// actually works across tabs and browser restarts.
//
// clearSessionState() — wipes in-memory + sessionStorage only. Safe on transient
//   failures (refresh 5xx) so the refresh_token stays for the next attempt.
// signOut()      — wipes everything including the refresh_token.

import React, { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { portalConfig } from './portal-config.js';

let inFlightRefresh: Promise<void> | null = null;

const STATE_KEY = 'borg_oauth_state';
const VERIFIER_KEY = 'borg_code_verifier';
const TOKEN_KEY = 'borg_token';
const REFRESH_KEY = 'borg_refresh';
const SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
// After a transient refresh failure (5xx / network) we retry on a short fixed
// delay. The normal near-expiry refresh schedule collapses to 0 once we're
// inside REFRESH_BUFFER_MS — without an explicit retry, a transient failure
// during the buffer window would silently let the id_token expire.
const TRANSIENT_RETRY_MS = 30 * 1000;

// True when a session is within REFRESH_BUFFER_MS of expiry (or already past
// it). Used by both the bootstrap path and the focus/visibility listener.
export function sessionNeedsRefresh(
  session: { expiresAt: number } | null,
  nowMs: number,
  bufferMs: number,
): boolean {
  if (!session) return false;
  return session.expiresAt <= nowMs + bufferMs;
}

interface StoredSession {
  idToken: string;
  email: string;
  expiresAt: number;
  sessionStartedAt: number;
}

interface RefreshEntry {
  refreshToken: string;
  sessionStartedAt: number;
}

interface AuthState {
  idToken: string | null;
  email: string | null;
  isLoading: boolean;
  signIn: () => Promise<void>;
  signOut: () => void;
  // Non-destructive recovery for transient 401s from the API. Attempts a
  // refresh_token grant; on success the id_token updates and gated React
  // Query queries re-run automatically. On failure the in-memory session
  // is cleared but the 7-day refresh_token is preserved, so the next
  // page-load (or Sign-in click) can silently re-auth without losing the
  // session anchor. Prefer this over signOut() when reacting to a 401 on
  // an API call.
  refreshSession: () => Promise<void>;
}

type Action =
  | { type: 'SET_SESSION'; session: StoredSession }
  | { type: 'CLEAR_SESSION' }
  | { type: 'LOADED' };

interface InternalState {
  idToken: string | null;
  email: string | null;
  isLoading: boolean;
}

function reducer(state: InternalState, action: Action): InternalState {
  switch (action.type) {
    case 'SET_SESSION':
      return { ...state, idToken: action.session.idToken, email: action.session.email, isLoading: false };
    case 'CLEAR_SESSION':
      return { idToken: null, email: null, isLoading: false };
    case 'LOADED':
      return { ...state, isLoading: false };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthState>({
  idToken: null,
  email: null,
  isLoading: true,
  signIn: async () => {},
  signOut: () => {},
  refreshSession: async () => {},
});

function parseIdToken(token: string): { email: string; exp: number } {
  const parts = token.split('.');
  if (parts.length < 2) throw new Error('bad id_token');
  let b64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const payload = JSON.parse(atob(b64)) as { email: string; exp: number };
  return { email: payload.email, exp: payload.exp };
}

async function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(hash);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(len: number): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '').slice(0, len);
}

function saveRefresh(entry: RefreshEntry): void {
  try { localStorage.setItem(REFRESH_KEY, JSON.stringify(entry)); } catch { /* quota/disabled */ }
}

function loadRefresh(): RefreshEntry | null {
  try {
    const raw = localStorage.getItem(REFRESH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RefreshEntry;
    if (typeof parsed?.refreshToken !== 'string' || typeof parsed?.sessionStartedAt !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
}

function clearRefresh(): void {
  try { localStorage.removeItem(REFRESH_KEY); } catch { /* disabled */ }
}

function cleanUrl(): void {
  history.replaceState(null, '', window.location.pathname);
}

function persistSession(session: StoredSession): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(session));
}

async function exchangeCode(apiBase: string, code: string, codeVerifier: string) {
  const res = await fetch(`${apiBase}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      code,
      code_verifier: codeVerifier,
      // The /auth/token Lambda requires redirect_uri to exactly equal the value
      // used when building the Google authorize URL in signIn() (origin + '/').
      // Omitting it (or sending a different value) makes the code grant 401.
      redirect_uri: window.location.origin + '/',
    }),
  });
  if (!res.ok) throw new Error('Token exchange failed');
  return (await res.json()) as { idToken: string; refreshToken?: string; expiresIn: number };
}

// Categorize refresh failures so doRefresh can decide whether the failure is
// recoverable (keep the user's session alive) or definitive (force re-auth).
// During a deploy, every Lambda — including the /auth/token handler — is
// briefly unavailable; treating that case as definitive logs the user out
// for no reason.
class RefreshTokenError extends Error {
  constructor(public readonly transient: boolean, message: string) {
    super(message);
    this.name = 'RefreshTokenError';
  }
}

function isTransientRefreshFailure(err: unknown): boolean {
  return err instanceof RefreshTokenError && err.transient;
}

async function exchangeRefresh(apiBase: string, refreshToken: string) {
  let res: Response;
  try {
    res = await fetch(`${apiBase}/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ grant_type: 'refresh_token', refresh_token: refreshToken }),
    });
  } catch {
    // Browser-level fetch failure (no HTTP response). Always transient: the
    // network blip resolves on its own, or the API comes back up.
    throw new RefreshTokenError(true, 'network error');
  }
  if (!res.ok) {
    // 5xx → API broken or mid-deploy (transient). 4xx → /auth/token actively
    // rejected the refresh_token (definitive — user must sign in again).
    throw new RefreshTokenError(res.status >= 500, `HTTP ${res.status}`);
  }
  return (await res.json()) as { idToken: string; expiresIn: number };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, { idToken: null, email: null, isLoading: true });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentSessionRef = useRef<StoredSession | null>(null);
  // doRefresh closes over component-scoped helpers and is re-created on every
  // render. Consumers put refreshSession in useEffect deps; an unstable
  // identity would cause those effects to re-fire on every parent render and
  // (with a still-present query.error) re-trigger refresh attempts. Route the
  // public refreshSession through a ref-backed stable wrapper so the context
  // exposes a referentially-stable function that always calls the latest
  // doRefresh.
  const doRefreshRef = useRef<() => Promise<void>>(async () => {});

  function clearSessionState(reason: string) {
    console.warn(`[auth-debug] clearSessionState: ${reason}`);
    if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    currentSessionRef.current = null;
    sessionStorage.removeItem(TOKEN_KEY);
    dispatch({ type: 'CLEAR_SESSION' });
  }

  function applySession(session: StoredSession) {
    currentSessionRef.current = session;
    persistSession(session);
    dispatch({ type: 'SET_SESSION', session });
    scheduleRefresh(session);
  }

  function scheduleRefresh(session: StoredSession, overrideDelayMs?: number) {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    const now = Date.now();
    let delay: number;
    if (overrideDelayMs !== undefined) {
      // Caller supplied an explicit retry delay (transient-failure backoff).
      // Cap at time-until-expiry so we never schedule past the point where the
      // in-memory id_token would already be unusable for direct API calls.
      delay = Math.min(overrideDelayMs, Math.max(0, session.expiresAt - now));
    } else {
      delay = session.expiresAt - now - REFRESH_BUFFER_MS;
    }
    if (delay <= 0) return;
    refreshTimerRef.current = setTimeout(() => { void doRefresh(); }, delay);
  }

  async function doRefresh(): Promise<void> {
    if (inFlightRefresh) return inFlightRefresh;
    inFlightRefresh = (async () => {
      try {
        const refresh = loadRefresh();
        if (!refresh) { clearSessionState('doRefresh:no-refresh-in-storage'); return; }

        const now = Date.now();
        if (now > refresh.sessionStartedAt + SESSION_TTL_MS) {
          clearSessionState('doRefresh:past-7d-hard-cap');
          clearRefresh();
          return;
        }

        const data = await exchangeRefresh(portalConfig.apiBase, refresh.refreshToken);
        const { email, exp } = parseIdToken(data.idToken);
        const hardCap = refresh.sessionStartedAt + SESSION_TTL_MS;
        const session: StoredSession = {
          idToken: data.idToken,
          email,
          expiresAt: Math.min(exp * 1000, hardCap),
          sessionStartedAt: refresh.sessionStartedAt,
        };
        applySession(session);
      } catch (err) {
        // Transient failure (5xx / network) with a still-live in-memory
        // session: keep the user signed in. Their id_token is still valid
        // until its `exp`, so direct API calls keep working once the API
        // recovers, and applySession() re-arms the auto-refresh timer.
        // Definitive failure (4xx from /auth/token = refresh_token rejected,
        // or session past the 7-day hard cap handled earlier) → wipe the
        // in-memory session; re-auth is genuinely required.
        const current = currentSessionRef.current;
        const transient = isTransientRefreshFailure(err);
        const hasCurrent = current !== null;
        const stillLive = current ? Date.now() < current.expiresAt : false;
        if (transient && current && Date.now() < current.expiresAt) {
          applySession(current);
          scheduleRefresh(current, TRANSIENT_RETRY_MS);
        } else {
          clearSessionState(`doRefresh:catch:definitive transient=${transient} hasCurrent=${hasCurrent} stillLive=${stillLive} err=${String((err as Error)?.message ?? err)}`);
        }
      } finally {
        inFlightRefresh = null;
      }
    })();
    return inFlightRefresh;
  }

  async function signIn(): Promise<void> {
    const oauthState = randomString(24);
    sessionStorage.setItem(STATE_KEY, oauthState);

    const codeVerifier = randomString(64);
    sessionStorage.setItem(VERIFIER_KEY, codeVerifier);

    const codeChallenge = await sha256Base64Url(codeVerifier);

    // NOTE: no `hd` hint. Borg allows MULTIPLE hosted domains
    // (portalConfig.allowedHostedDomains), so we must not pin the Google
    // authorize URL to a single domain; the API-side authorizer enforces the
    // allow-list after sign-in.
    const params = new URLSearchParams({
      client_id: portalConfig.googleClientId,
      response_type: 'code',
      scope: 'openid email profile',
      redirect_uri: window.location.origin + '/',
      state: oauthState,
      access_type: 'offline',
      // Without `prompt=consent` Google reuses any existing grant for this
      // (client_id, user) pair and returns ONLY an id_token — no refresh_token.
      // `access_type=offline` requests offline access but does not force
      // re-issuance, so the second-and-subsequent sign-in returns a session
      // that's "born dead": once the id_token expires, doRefresh sees no
      // stored refresh_token and clearSessionState boots the user.
      // `prompt=consent` forces Google to always re-issue.
      prompt: 'consent',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  function signOut() {
    console.warn('[auth-debug] signOut() called from UI');
    clearSessionState('signOut');
    clearRefresh();
  }

  // Keep the ref pointing at the latest doRefresh closure each render so
  // the stable wrapper exposed via context always invokes the current
  // implementation.
  doRefreshRef.current = doRefresh;
  const refreshSession = useCallback(() => doRefreshRef.current(), []);

  // The near-expiry refresh is a setTimeout, which browsers throttle (and
  // suspend across laptop sleep) in a backgrounded tab. A long-lived tab can
  // therefore drift past its id_token's expiry without the timer firing; the
  // next API call then hits the authorizer with a dead token (a 403).
  // Refreshing the moment the tab regains focus/visibility — when the
  // in-memory session is at/near expiry — closes that gap. doRefresh's
  // inFlightRefresh guard keeps this from racing the scheduled timer.
  useEffect(() => {
    function onForeground() {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (sessionNeedsRefresh(currentSessionRef.current, Date.now(), REFRESH_BUFFER_MS)) {
        void doRefreshRef.current();
      }
    }
    window.addEventListener('focus', onForeground);
    document.addEventListener('visibilitychange', onForeground);
    return () => {
      window.removeEventListener('focus', onForeground);
      document.removeEventListener('visibilitychange', onForeground);
    };
  }, []);

  useEffect(() => {
    void (async () => {
      // Local-dev shortcut. portalConfig.googleClientId is rewritten to a real
      // value at deploy time by CDK; in `vite dev` it's the literal
      // 'DEV-PLACEHOLDER' injected by the vite plugin in vite.config.ts. When
      // we see the placeholder, skip the full Google OAuth handshake (which
      // would fail against a fake client ID) and synthesize a session so all
      // routes render. No sessionStorage persistence, no refresh timer — this
      // is purely for local UI work and never reaches production.
      if (portalConfig.googleClientId === 'DEV-PLACEHOLDER') {
        const now = Date.now();
        const fakeSession: StoredSession = {
          idToken: 'DEV-PLACEHOLDER', // allowlist-secret: literal placeholder, no auth value
          email: 'dev@localhost',
          expiresAt: now + 7 * 24 * 3600 * 1000,
          sessionStartedAt: now,
        };
        dispatch({ type: 'SET_SESSION', session: fakeSession });
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);

      const errorState = urlParams.get('state');
      const storedState = sessionStorage.getItem(STATE_KEY);
      if (urlParams.has('error') && storedState && errorState === storedState) {
        console.warn('[auth-debug] useEffect:oauth-error-from-google', { error: urlParams.get('error') });
        cleanUrl();
        sessionStorage.removeItem(STATE_KEY);
        sessionStorage.removeItem(VERIFIER_KEY);
        dispatch({ type: 'LOADED' });
        return;
      }

      const code = urlParams.get('code');
      const oauthState = urlParams.get('state');
      if (code && oauthState) {
        const savedState = sessionStorage.getItem(STATE_KEY);
        const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
        sessionStorage.removeItem(STATE_KEY);
        sessionStorage.removeItem(VERIFIER_KEY);

        if (oauthState !== savedState || !codeVerifier) {
          console.warn('[auth-debug] useEffect:state-mismatch-or-no-verifier', {
            hasSavedState: !!savedState, stateMatches: oauthState === savedState, hasVerifier: !!codeVerifier,
          });
          cleanUrl();
          dispatch({ type: 'LOADED' });
          return;
        }

        try {
          const tokens = await exchangeCode(portalConfig.apiBase, code, codeVerifier);
          const { email, exp } = parseIdToken(tokens.idToken);
          const now = Date.now();

          const existingRefresh = loadRefresh();
          let sessionStartedAt: number;
          if (tokens.refreshToken) {
            sessionStartedAt = now;
            saveRefresh({ refreshToken: tokens.refreshToken, sessionStartedAt });
          } else if (existingRefresh && now <= existingRefresh.sessionStartedAt + SESSION_TTL_MS) {
            sessionStartedAt = existingRefresh.sessionStartedAt;
          } else {
            sessionStartedAt = now;
          }

          const session: StoredSession = {
            idToken: tokens.idToken,
            email,
            expiresAt: Math.min(exp * 1000, sessionStartedAt + SESSION_TTL_MS),
            sessionStartedAt,
          };
          cleanUrl();
          applySession(session);
        } catch (err) {
          console.warn('[auth-debug] useEffect:exchangeCode-failed', {
            err: String((err as Error)?.message ?? err),
          });
          cleanUrl();
          dispatch({ type: 'LOADED' });
        }
        return;
      }

      const stored = sessionStorage.getItem(TOKEN_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as StoredSession;
          const now = Date.now();
          if (now > parsed.sessionStartedAt + SESSION_TTL_MS) {
            sessionStorage.removeItem(TOKEN_KEY);
            clearRefresh();
            dispatch({ type: 'LOADED' });
          } else if (sessionNeedsRefresh(parsed, now, REFRESH_BUFFER_MS)) {
            currentSessionRef.current = parsed;
            await doRefresh();
          } else {
            applySession(parsed);
          }
          return;
        } catch (err) {
          console.warn('[auth-debug] useEffect:bootstrap:stored-parse-failed', {
            err: String((err as Error)?.message ?? err),
          });
          sessionStorage.removeItem(TOKEN_KEY);
        }
      }

      const refresh = loadRefresh();
      if (refresh) {
        const now = Date.now();
        if (now > refresh.sessionStartedAt + SESSION_TTL_MS) {
          clearRefresh();
          dispatch({ type: 'LOADED' });
        } else {
          await doRefresh();
        }
        return;
      }

      dispatch({ type: 'LOADED' });
    })();
    return () => {
      // Clear any in-flight refresh timer on unmount so it doesn't fire
      // against a torn-down provider (matters for hot reload in dev and for
      // test isolation — the transient-failure retry uses a 30s timer that
      // can outlive a typical test).
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ idToken: state.idToken, email: state.email, isLoading: state.isLoading, signIn, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
